import * as React from 'react'
import { Copy } from 'lucide-react'
import type { EventSalesDto } from '@lib/dto/sales-packet.dto'
import type { PaymentRecordDto, SalesPaymentDto } from '@lib/dto/sales-payment.dto'
import { PAYMENT_STATUS_LABELS, SALES_RECORD_METHOD_LABELS, type OutsidePaymentMethod, type PaymentStatus } from '@lib/enums/sales-payment.enum'
import { dollarsFromCents } from '@lib/helpers/format'
import { depositReturnInputSchema, outsidePaymentInputSchema, refundInputSchema } from '@lib/input/sales-payment.input'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useRecordPayment, useRefund, useReturnDeposit } from '@/hooks/useSales'
import { todayIso } from '@/lib/bookedDays'
import { formatEventDate } from '@/lib/eventTime'
import { centsToInput, inputToCents } from '@/lib/price'

export const PAYMENT_TONES: Readonly<Record<PaymentStatus, BadgeTone>> = {
  upcoming: 'neutral',
  due: 'warning',
  late: 'danger',
  processing: 'info',
  paid: 'success',
  stopped: 'neutral',
  canceled: 'neutral',
}

/** "Due on signing", or the day it's due. */
export function dueLabel(dueOn: CalendarDay | null): string {
  return dueOn === null ? 'Due on signing' : `Due ${formatEventDate(dueOn)}`
}

/** Copy a payment's link, to send to whoever is paying it. */
export function CopyPayLink({ payPath }: { payPath: string }) {
  const { toast } = useToast()
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(`${window.location.origin}${payPath}`).then(
          () => toast('Payment link copied.'),
          () => toast('That link couldn’t be copied. Try again.'),
        )
      }}
    >
      <Copy className="h-3.5 w-3.5" />
      Copy link
    </Button>
  )
}

function methodOf(value: string): OutsidePaymentMethod {
  return value === 'transfer' || value === 'other' ? value : 'check'
}

/** Record a payment the venue received outside the app: a check, a wire. */
function RecordPayment({ eventId, payment, onClose }: { eventId: string; payment: SalesPaymentDto; onClose: () => void }) {
  const { toast } = useToast()
  const record = useRecordPayment(eventId)
  const [method, setMethod] = React.useState<OutsidePaymentMethod>('check')
  const [amount, setAmount] = React.useState(centsToInput(Math.max(0, payment.amountCents - payment.creditedCents)))
  const [receivedOn, setReceivedOn] = React.useState<CalendarDay | ''>(todayIso())
  const [reference, setReference] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  function handleSave(): void {
    const cents = inputToCents(amount)
    if (cents === null || cents === 0) return setError('Enter the amount received, in dollars.')
    if (receivedOn === '') return setError('Choose the day it was received.')
    const parsed = outsidePaymentInputSchema.safeParse({ method, amountCents: cents, receivedOn, reference })
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Something here isn’t complete.')
    setError(null)
    record.mutate(
      { paymentId: payment.id, ...parsed.data },
      {
        onSuccess: () => {
          toast('Payment recorded. A receipt is on its way to the couple.')
          onClose()
        },
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record a payment</DialogTitle>
          <DialogDescription>
            Toward {payment.label}: one received outside the app. The couple gets a receipt.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="How it was paid" htmlFor="record-method">
            <Select id="record-method" value={method} onChange={(e) => setMethod(methodOf(e.target.value))}>
              <option value="check">{SALES_RECORD_METHOD_LABELS.check}</option>
              <option value="transfer">{SALES_RECORD_METHOD_LABELS.transfer}</option>
              <option value="other">{SALES_RECORD_METHOD_LABELS.other}</option>
            </Select>
          </Field>
          <Field label="Amount ($)" htmlFor="record-amount">
            <Input id="record-amount" inputMode="decimal" className="tabular-nums" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label="Received on" htmlFor="record-on">
            <DatePicker id="record-on" value={receivedOn} onChange={setReceivedOn} max={todayIso()} />
          </Field>
          <Field label="Reference" htmlFor="record-reference" hint="A check number, say.">
            <Input id="record-reference" value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        </div>
        {error === null ? null : <FormError>{error}</FormError>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={record.isPending}>
            {record.isPending ? 'Saving…' : 'Record payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function shortDate(instant: string): string {
  return new Date(instant).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** How a record was paid: "Check No. 1041", "Card". */
function howPaid(record: PaymentRecordDto): string {
  return record.reference === '' ? SALES_RECORD_METHOD_LABELS[record.method] : `${SALES_RECORD_METHOD_LABELS[record.method]} ${record.reference}`
}

/** What was refunded from a record, if anything: "refunded $500". A security deposit's return has its own line. */
function refundedNote(record: PaymentRecordDto): string {
  const back = record.refunds.filter((f) => f.kind === 'refund' && f.status !== 'failed').reduce((sum, f) => sum + f.amountCents, 0)
  return back === 0 ? '' : ` (refunded ${dollarsFromCents(back)})`
}

/** What one payment has had toward it, in a line: "Check No. 1041, $3,000, Oct 1, 2026 (refunded $500)". */
function recordsLine(payment: SalesPaymentDto): string | null {
  const done = payment.records.filter((r) => r.status === 'succeeded' || r.status === 'processing')
  if (done.length === 0) return null
  return done
    .map((r) => {
      const when = r.receivedAt === null ? 'on its way' : shortDate(r.receivedAt)
      return `${howPaid(r)}, ${dollarsFromCents(r.amountCents + r.discountCents)}, ${when}${refundedNote(r)}`
    })
    .join(' · ')
}

/** A settled security deposit: "Returned $400: A broken chair", or "Kept: …". */
function returnLine(payment: SalesPaymentDto): string | null {
  const settled = payment.depositReturn
  if (settled === null) return null
  const what = settled.returnedCents === 0 ? 'Kept' : `Returned ${dollarsFromCents(settled.returnedCents)}`
  return `${what} on ${shortDate(settled.at)}: ${settled.reason}`
}

/** A security deposit's money that can still go back: what arrived, less what went back. */
function heldOf(payment: SalesPaymentDto): number {
  return payment.records.reduce((sum, r) => sum + r.refundableCents, 0)
}

/** The reason box both dialogs share: the couple read it. */
function ReasonField({ id, value, onChange }: { id: string; value: string; onChange: (value: string) => void }) {
  return (
    <Field label="Reason" htmlFor={id} hint="The couple are told this.">
      <Textarea id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  )
}

/** One record as the refund dialog offers it: "Card, $3,000, Oct 1, 2026: up to $2,500". */
function refundOption(record: PaymentRecordDto): string {
  const when = record.receivedAt === null ? '' : `, ${shortDate(record.receivedAt)}`
  return `${howPaid(record)}, ${dollarsFromCents(record.amountCents)}${when}: up to ${dollarsFromCents(record.refundableCents)}`
}

/** Give back all or part of a payment: Stripe returns a card or bank payment; one received by hand is recorded. */
function RefundPayment({ eventId, payment, onClose }: { eventId: string; payment: SalesPaymentDto; onClose: () => void }) {
  const { toast } = useToast()
  const refund = useRefund(eventId)
  const records = payment.records.filter((r) => r.refundableCents > 0)
  const [recordId, setRecordId] = React.useState(records[0]?.id ?? '')
  const record = records.find((r) => r.id === recordId) ?? records[0]
  const [amount, setAmount] = React.useState(centsToInput(record?.refundableCents ?? 0))
  const [reason, setReason] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const byHand = record !== undefined && (record.method === 'check' || record.method === 'transfer' || record.method === 'other')

  function handleRecord(id: string): void {
    setRecordId(id)
    setAmount(centsToInput(records.find((r) => r.id === id)?.refundableCents ?? 0))
  }

  function handleRefund(): void {
    if (record === undefined) return
    const cents = inputToCents(amount)
    if (cents === null || cents === 0) return setError('Enter the amount to refund, in dollars.')
    if (cents > record.refundableCents) return setError(`At most ${dollarsFromCents(record.refundableCents)} of this payment can be refunded.`)
    const parsed = refundInputSchema.safeParse({ amountCents: cents, reason })
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Something here isn’t complete.')
    setError(null)
    refund.mutate(
      { paymentId: payment.id, recordId: record.id, ...parsed.data },
      {
        onSuccess: () => {
          toast(`Refunded ${dollarsFromCents(cents)}. The couple have been told.`)
          onClose()
        },
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refund a payment</DialogTitle>
          <DialogDescription>
            From {payment.label}. Whoever paid and the couple are told, with your reason. It isn’t owed again afterwards.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          {records.length > 1 ? (
            <Field label="Which payment" htmlFor="refund-record">
              <Select id="refund-record" value={recordId} onChange={(e) => handleRecord(e.target.value)}>
                {records.map((r) => (
                  <option key={r.id} value={r.id}>
                    {refundOption(r)}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          <Field
            label="Amount ($)"
            htmlFor="refund-amount"
            hint={byHand ? 'This was paid outside the app: it’s recorded here, and you give the money back.' : 'It goes back to the card or bank account it came from.'}
          >
            <Input id="refund-amount" inputMode="decimal" className="tabular-nums" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <ReasonField id="refund-reason" value={reason} onChange={setReason} />
        </div>
        {error === null ? null : <FormError>{error}</FormError>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleRefund} disabled={refund.isPending}>
            {refund.isPending ? 'Refunding…' : 'Refund'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Settle a security deposit after the event: return all, some or none of it, saying why. */
function ReturnDeposit({ eventId, payment, onClose }: { eventId: string; payment: SalesPaymentDto; onClose: () => void }) {
  const { toast } = useToast()
  const settle = useReturnDeposit(eventId)
  const held = heldOf(payment)
  const [amount, setAmount] = React.useState(centsToInput(held))
  const [reason, setReason] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const keeping = inputToCents(amount) === 0

  function buttonLabel(): string {
    if (settle.isPending) return 'Saving…'
    return keeping ? 'Keep the deposit' : 'Return it'
  }

  function handleReturn(): void {
    const cents = inputToCents(amount)
    if (cents === null) return setError('Enter the amount to return, in dollars, or 0 to keep it.')
    if (cents > held) return setError(`At most ${dollarsFromCents(held)} is held on this security deposit.`)
    const parsed = depositReturnInputSchema.safeParse({ amountCents: cents, reason })
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Something here isn’t complete.')
    setError(null)
    settle.mutate(
      { paymentId: payment.id, ...parsed.data },
      {
        onSuccess: () => {
          toast(cents === 0 ? 'Kept the security deposit. The couple have been told.' : `Returned ${dollarsFromCents(cents)}. The couple have been told.`)
          onClose()
        },
        onError: (e) => setError(requestErrorMessage(e)),
      },
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Return the security deposit</DialogTitle>
          <DialogDescription>
            {dollarsFromCents(held)} is held. Return all of it, part, or none, once. The couple are told, with your reason.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Amount to return ($)" htmlFor="return-amount" hint="Enter 0 to keep all of it.">
            <Input id="return-amount" inputMode="decimal" className="tabular-nums" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <ReasonField id="return-reason" value={reason} onChange={setReason} />
        </div>
        {error === null ? null : <FormError>{error}</FormError>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleReturn} disabled={settle.isPending}>
            {buttonLabel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** What's agreed and what's come in, tax and security deposits apart (docs/sales.spec.md, "Revenue"). */
function RevenueLine({ sales }: { sales: EventSalesDto }) {
  const { revenue } = sales
  const deposits = sales.payments.some((p) => p.kind === 'security_deposit')
  return (
    <div className="space-y-0.5 text-right">
      <p className="m-0 text-sm text-ink-muted">
        Agreed {dollarsFromCents(revenue.agreedCents)} (tax {dollarsFromCents(revenue.taxCents)}) · Collected {dollarsFromCents(revenue.collectedCents)} · Refunded{' '}
        {dollarsFromCents(revenue.refundedCents)} · Still due {dollarsFromCents(revenue.stillDueCents)}
      </p>
      {deposits ? <p className="m-0 text-xs text-ink-muted">Security deposits held {dollarsFromCents(revenue.depositsHeldCents)}</p> : null}
    </div>
  )
}

type Acting = { kind: 'record' | 'refund' | 'return'; payment: SalesPaymentDto } | null

/** The buttons a payment row offers, as far as the person may use them. */
function RowActions({ payment, sales, onAct }: { payment: SalesPaymentDto; sales: EventSalesDto; onAct: (kind: 'record' | 'refund' | 'return') => void }) {
  const open = payment.status !== 'paid' && payment.status !== 'stopped' && payment.status !== 'canceled'
  const returnable = payment.kind === 'security_deposit' && sales.canEdit && sales.depositsReturnable && payment.depositReturn === null && heldOf(payment) > 0
  const refundable = sales.canRefund && payment.records.some((r) => r.refundableCents > 0)
  return (
    <span className="flex gap-1">
      {open ? <CopyPayLink payPath={payment.payPath} /> : null}
      {open && sales.canEdit ? (
        <Button variant="outline" size="sm" onClick={() => onAct('record')}>
          Record a payment
        </Button>
      ) : null}
      {returnable ? (
        <Button variant="outline" size="sm" onClick={() => onAct('return')}>
          Return deposit
        </Button>
      ) : null}
      {refundable ? (
        <Button variant="ghost" size="sm" onClick={() => onAct('refund')}>
          Refund
        </Button>
      ) : null}
    </span>
  )
}

/**
 * The event's payments for staff (docs/sales.spec.md, "Paying"): each one's
 * amount, due day and status, its link to send, recording one received
 * outside the app, refunds, and returning a security deposit after the event.
 * Revenue counts installments; security deposits sit apart.
 */
export function PaymentsPanel({ eventId, sales }: { eventId: string; sales: EventSalesDto }) {
  const [acting, setActing] = React.useState<Acting>(null)
  if (sales.payments.length === 0) return null
  const close = () => setActing(null)
  return (
    <Card asChild className="space-y-3 p-4">
      <section>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="m-0 text-sm font-semibold text-ink">Payments</h3>
          <RevenueLine sales={sales} />
        </div>
        {sales.autopay === null ? null : <p className="m-0 text-sm text-ink-muted">Autopay is on with the couple’s {sales.autopay.label}.</p>}
        <ul className="m-0 list-none divide-y divide-line p-0">
          {sales.payments.map((p) => {
            const settled = returnLine(p)
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2.5">
                <div className="min-w-40 flex-1">
                  <p className="m-0 text-sm text-ink">{p.label}</p>
                  <p className="m-0 text-xs text-ink-muted">{recordsLine(p) ?? dueLabel(p.dueOn)}</p>
                  {settled === null ? null : <p className="m-0 text-xs text-ink-muted">{settled}</p>}
                </div>
                <span className="w-24 text-right text-sm tabular-nums text-ink">{dollarsFromCents(p.amountCents)}</span>
                <span className="w-24">
                  <Badge tone={PAYMENT_TONES[p.status]}>{PAYMENT_STATUS_LABELS[p.status]}</Badge>
                </span>
                <RowActions payment={p} sales={sales} onAct={(kind) => setActing({ kind, payment: p })} />
              </li>
            )
          })}
        </ul>
        {acting?.kind === 'record' ? <RecordPayment eventId={eventId} payment={acting.payment} onClose={close} /> : null}
        {acting?.kind === 'refund' ? <RefundPayment eventId={eventId} payment={acting.payment} onClose={close} /> : null}
        {acting?.kind === 'return' ? <ReturnDeposit eventId={eventId} payment={acting.payment} onClose={close} /> : null}
      </section>
    </Card>
  )
}
